import type {
  CSSProperties,
  FC,
  MutableRefObject,
  ReactNode,
  Ref
} from 'react';
import { observer } from 'mobx-react';
import { Picture } from '@ui';
import { inputCross } from '@images';
import type { storeAddressT } from '@store/UserStore.type';
import type { PrepareAllCommentsOptionsReturnType } from '@utils/orderUtils/prepareAllCommentsOptions';
import OrderRegisterCustomMap from '@components/OrderRegistration/components/OrderRegisterCustomMap';
import ModalHeader from '../components/ModalHeader';
import ModalButtonBar from '../components/ModalButtonBar';
import MapToggle from '../components/MapToggle';
import FlatInput from '../components/FlatInput';
import AddressOptions from '../components/AddressOptions';
import { FioInput, PhoneInput } from '../components/UserDataInputs';
import type { ModalState } from '../types';

interface AddressFormStageProps {
  isMobile: boolean;
  modalStage: Extract<ModalState, 'addAddress' | 'editAddress'>;
  mobileFormWrapperStyle: CSSProperties;
  mobileFormMapHeight: string;
  isOpenMap: boolean;
  setIsOpenMap: (v: boolean) => void;
  // Map props
  mapRef: ((instance: Ref<any>) => void) | MutableRefObject<any> | undefined;
  coords: number[];
  editAddress: boolean;
  iconContentTemplate: any;
  isDefaultMark: () => boolean;
  selectedAddress: number | null;
  selectAddress: (item: storeAddressT) => void;
  onMapClick: (e: any, mapRefName: string) => void;
  onLoadMap: (ymaps: any) => void;
  address: string | null;
  // Form state
  flat: string;
  setFlat: (v: string) => void;
  fio: string;
  setFio: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  comment: string;
  setComment: (v: string) => void;
  isShowComment: boolean;
  toggleShowComment: () => void;
  isMainAddress: boolean;
  selectMainAddress: () => void;
  // Comments layer
  allCommentsOptions: Partial<PrepareAllCommentsOptionsReturnType>;
  changeNewParams: (
    type: string,
    value: boolean | number | string,
    index?: number
  ) => void;
  // Parent render-prop slots
  addressComponent: (flatBlock: ReactNode) => ReactNode;
  userComponent: (fioBlock: ReactNode, phoneBlock: ReactNode) => ReactNode;
  // Actions
  onClose: () => void;
  onCancel: () => void;
  onNext: () => void;
  onSave: () => void;
}

const AddressFormStage: FC<AddressFormStageProps> = observer(
  ({
    isMobile,
    modalStage,
    mobileFormWrapperStyle,
    mobileFormMapHeight,
    isOpenMap,
    setIsOpenMap,
    mapRef,
    coords,
    editAddress,
    iconContentTemplate,
    isDefaultMark,
    selectedAddress,
    selectAddress,
    onMapClick,
    onLoadMap,
    address,
    flat,
    setFlat,
    fio,
    setFio,
    phone,
    setPhone,
    comment,
    setComment,
    isShowComment,
    toggleShowComment,
    isMainAddress,
    selectMainAddress,
    allCommentsOptions,
    changeNewParams,
    addressComponent,
    userComponent,
    onClose,
    onCancel,
    onNext,
    onSave
  }) => {
    const { checkboxes, selects, orderHome } = allCommentsOptions;

    const flatBlock = <FlatInput value={flat} onChange={setFlat} />;
    const fioBlock = <FioInput value={fio} onChange={setFio} />;
    const phoneBlock = <PhoneInput value={phone} onChange={setPhone} />;

    const addressOptions = (
      <AddressOptions
        orderHome={orderHome}
        comment={comment}
        onCommentChange={setComment}
        isShowComment={isShowComment}
        onToggleComment={toggleShowComment}
        checkboxes={checkboxes ?? []}
        selects={selects ?? []}
        isMainAddress={isMainAddress}
        onSelectMainAddress={selectMainAddress}
        changeNewParams={changeNewParams}
      />
    );

    if (isMobile) {
      const isShowMapMarkPlace =
        (modalStage === 'addAddress' && !!address) ||
        modalStage === 'editAddress';

      return (
        <div className='left'>
          <div className='mobileFields' style={mobileFormWrapperStyle}>
            <ModalHeader
              title={
                modalStage === 'addAddress' ? 'Новый адрес' : 'Изменить адрес'
              }
              right={
                <Picture
                  icon={inputCross}
                  height='20'
                  alt=''
                  onClick={onClose}
                />
              }
            />
            <MapToggle isOpenMap={isOpenMap} onChange={setIsOpenMap} />

            {isOpenMap ? (
              <OrderRegisterCustomMap
                mapRef={mapRef}
                onMapClick={onMapClick}
                onLoadMap={onLoadMap}
                coords={coords}
                editAddress={editAddress}
                iconContentTemplate={iconContentTemplate}
                isShowMapMarkPlace={isShowMapMarkPlace}
                isDefaultMark={isDefaultMark}
                selectedAddress={selectedAddress}
                selectAddress={selectAddress}
                width='100%'
                height={mobileFormMapHeight}
                mapRefName='mapRefModal'
                isShowClusterer
              />
            ) : (
              <>
                <div className='address-info'>
                  <div className='mb-4 text-sm font-medium'>Адрес</div>
                  {addressComponent(flatBlock)}
                  {addressOptions}
                </div>
                <div className='address'>Получатель</div>
                {userComponent(fioBlock, phoneBlock)}
              </>
            )}
          </div>
          <ModalButtonBar
            variant='add-new-address'
            cancelLabel='Отменить'
            onCancel={onCancel}
            confirmLabel='Сохранить'
            onConfirm={onSave}
          />
        </div>
      );
    }

    return (
      <div className='left'>
        <div>
          <ModalHeader
            title={
              modalStage === 'addAddress'
                ? 'Новый адрес доставки'
                : 'Изменить адрес'
            }
            right={
              <>
                <span className='blacked'>1</span>
                /2
              </>
            }
            rightClassName='page'
          />
          <div className='address'>Адрес</div>
          <div className='address-info'>
            {addressComponent(flatBlock)}
            {addressOptions}
          </div>
        </div>
        <ModalButtonBar
          variant='add-new-address'
          cancelLabel='Назад'
          onCancel={onCancel}
          confirmLabel='Далее'
          onConfirm={onNext}
        />
      </div>
    );
  }
);

export default AddressFormStage;
